import React from 'react';
import { createApi } from '@reduxjs/toolkit/query/react';
import { SSC_SEM_API_URL } from '../settings/config';
import { authRequest } from '../store/authorizedRequests';
import {
	Backlink,
	BacklinksOverviewResult,
	BacklinksResource,
	BacklinksResourcePaths,
	FetchOverviewQueryParams,
	FetchResourceQueryParams,
	ReferringDomain,
	TldDistribution,
	TopAnchor,
	TopCountry,
} from './types';

const baseUrl = `${SSC_SEM_API_URL}/backlinks`;

const RESOURCE_REFETCH_TIME = 7200;

/**
 * @param resourceType A type of backlinks resource
 * @returns a string defining the endpoint for the given resource type
 */
const getApiUrlPath = (resourceType: keyof BacklinksResourcePaths) => {
	let paths: BacklinksResourcePaths = {
		backlinks: '',
		referring_domains: '/referring-domains',
		top_anchors: '/top-anchors',
		tld_distribution: '/tld-distribution',
		top_countries: '/top-countries',
	};

	return paths[resourceType] || '';
};

export const backlinksApi = createApi({
	reducerPath: 'api.backlinks',

	keepUnusedDataFor: RESOURCE_REFETCH_TIME,

	baseQuery: async (args, api, extraOptions) => {
		try {
			const data = await authRequest(baseUrl.concat(args), api.dispatch, api.getState);
			return { data };
		} catch (error) {
			return { error };
		}
	},

	endpoints: (builder) => ({
		fetchOverview: builder.query<BacklinksOverviewResult, FetchOverviewQueryParams>({
			query: ({ domain, startDate, endDate }) => `/overview?domain=${domain}&start_date=${startDate}&end_date=${endDate}`,
		}),

		fetchResource: builder.query<BacklinksResource<Backlink | ReferringDomain | TopAnchor | TldDistribution | TopCountry>, FetchResourceQueryParams>({
			query: ({ resourceType, domain, orderBy, orderDir, offset = 0 }) => {
				const resourcePath = getApiUrlPath(resourceType);
				return `${resourcePath}?domain=${domain}&order_by=${orderBy}&order_dir=${orderDir}&offset=${offset}`;
			},

			// The data we get from any endpoint has a different 'main data' property: backlinks or tld_... etc.
			// This function transforms it into something generic
			transformResponse: (response, meta, arg) => {
				return {
					data: response[arg.resourceType],
					has_more: response.has_more,
				};
			},

			// Function that serializes the query args in order to create a cache key.
			// This cache key will be used when comparing two requests: if the same cache key
			// is generated the request is deduped with the already cached data, or
			// in our case it will go through the merge function and be saved together
			// Will return the query args that define a different cache key.
			// The offset parameter is not used, so queries with different offsets will merge together
			serializeQueryArgs: ({ queryArgs, endpointDefinition, endpointName }) => {
				const { resourceType, domain, orderBy, orderDir } = queryArgs;
				return { resourceType, domain, orderBy, orderDir };
			},

			// Function that will decide if we should fetch new data if the
			// query arguments serialize to the same cache key (so when the offset is different).
			// Used to handle pagination of the backlinks data,
			// we will force-fetch if we need data from a bigger offset than previously fetched
			forceRefetch: ({ currentArg, previousArg, state, endpointState }) => {
				// currentArg should always be defined
				if (typeof currentArg === 'undefined') {
					return false;
				}

				// If there was not a previous fetch then fetch
				if (typeof previousArg === 'undefined') {
					return true;
				}

				// Refetch only if the current offset is greater
				return currentArg.offset > previousArg.offset;
			},

			// Function that will decide what to do with the new response data. In our case
			// we need to add it to the data that is already cached
			merge: (currentCacheData, responseData, otherArgs) => {
				return {
					data: currentCacheData.data.concat(responseData.data),
					has_more: responseData.has_more,
				};
			},
		}),
	}),
});

export const { useFetchOverviewQuery, useLazyFetchOverviewQuery, useFetchResourceQuery, useLazyFetchResourceQuery } = backlinksApi;
